import { useEffect } from 'react';
import { Button } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './EmailVerifiedAccountMismatch.less';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TokenService } from '../../services/secureTokenService';
import { CirraServiceType } from '../../enums/auth';
import { getServiceHomePath, getServiceHomeUrl } from '../../constants/route';
import { trackEvent } from '../../utils/monitoring';

interface LocationState {
  currentEmail?: string;
  verifiedEmail?: string;
  service?: CirraServiceType;
}

const EmailVerifiedAccountMismatch = () => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const currentEmail = state?.currentEmail || 'your current account';
  const verifiedEmail = state?.verifiedEmail || 'the verified email';
  const service = state?.service || CirraServiceType.MCP;

  // Enable scrolling for this page (override global overflow: hidden)
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleContinueWithCurrent = () => {
    trackEvent('Email Verification Mismatch - Continue Current', {
      current_email_domain: state?.currentEmail?.split('@')[1],
    });

    // Clear verification-related localStorage
    localStorage.removeItem('signup_email');
    localStorage.removeItem('signup_session_id');
    localStorage.removeItem('signup_correlation_id');

    // Navigate to the appropriate app home based on service context
    navigate(getServiceHomePath(service).path, { replace: true });
  };

  const handleLogoutAndSignIn = () => {
    trackEvent('Email Verification Mismatch - Logout', {
      verified_email_domain: state?.verifiedEmail?.split('@')[1],
    });

    // Clear verification-related localStorage to prevent stale mismatch detection
    localStorage.removeItem('signup_email');
    localStorage.removeItem('signup_session_id');
    localStorage.removeItem('signup_correlation_id');

    TokenService.clearTokens();
    logout({
      logoutParams: {
        returnTo: getServiceHomeUrl(service),
      },
    });
  };

  return (
    <div id="email-verified-mismatch-container">
      <div className="email-verified-icon">
        <Logo />
        <CheckCircleOutlined className="success-icon" />
      </div>
      <h1>Email Verified Successfully!</h1>
      <p className="success-message">Your email address has been verified.</p>

      <div className="mismatch-notice">
        <InfoCircleOutlined className="info-icon" />
        <p>However, you are currently logged in with a different account.</p>
      </div>

      <div className="email-comparison">
        <div className="email-card current">
          <span className="label">Currently logged in as:</span>
          <span className="email">{currentEmail}</span>
        </div>
        <div className="email-card verified">
          <span className="label">Email just verified:</span>
          <span className="email">{verifiedEmail}</span>
        </div>
      </div>

      <p className="action-prompt">
        Would you like to continue with your current account or log in with the
        newly verified email?
      </p>

      <div className="button-group">
        <Button
          size="large"
          onClick={handleContinueWithCurrent}
          className="continue-button"
        >
          Continue with Current Account
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleLogoutAndSignIn}
          className="login-button"
        >
          Log Out and Sign In
        </Button>
      </div>
    </div>
  );
};

export default EmailVerifiedAccountMismatch;
